import { useCallback, useEffect, useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Body1,
  Caption1,
  Button,
  Card,
  Combobox,
  Dropdown,
  Option,
  Textarea,
  Input,
  Label,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Spinner,
  Dialog,
  DialogSurface,
} from '@fluentui/react-components';
import {
  AddRegular,
  CheckmarkRegular,
  DocumentBulletListRegular,
  InfoRegular,
  LightbulbRegular,
  ShieldCheckmarkRegular,
  Dismiss24Regular,
  CalendarRegular,
  SparkleRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';
import { useAuth } from '@/hooks/useAuth';
import { useDiscoveryStore } from '@/features/discovery/stores/discoveryStore';
import { ReportRunDialog } from '@/features/discovery/components/ReportRunDialog/ReportRunDialog';
import { getProfile, checkRateLimit } from '@/features/discovery/services/discoveryApi';
import { useGraphConsent } from '@/features/discovery/hooks/useGraphConsent';
import { useIndustryQuestions } from '@/features/discovery/hooks/useIndustryQuestions';
import { SitePicker } from '@/features/discovery/components/SitePicker/SitePicker';
import { PageBanner } from '@/components/common/PageBanner';
import { environmentScanEnabled } from '@/config/features';
import {
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  USER_ROLE_OPTIONS,
  type Industry,
  type SiteSummary,
  type SuggestedQuestion,
  type UserContext,
} from '@/features/discovery/types/discovery.types';

const COUNTRY_OPTIONS = [
  'Australia',
  'New Zealand',
  'United Kingdom',
  'United States',
  'Canada',
  'Singapore',
  'Other',
] as const;

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  // Shared eyebrow for card heads / prompt groups (mono, on white surfaces).
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 65fr) minmax(0, 35fr)',
    gap: tokens.spacingHorizontalXXL,
    alignItems: 'start',
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  colMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    minWidth: 0,
  },
  // Flat hairline card (HTML .rdy-card): white, 1px #E8E8E8, small radius, no shadow.
  sectionCard: {
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: 'none',
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  cardHead: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
  },
  cardTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    color: transformrBrand.navy,
  },
  cardDesc: {
    color: transformrBrand.grey700,
  },
  formCard: {
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: 'none',
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    // ── "Console" form language (see .github/context/readiness-form-design) ──
    // Mono uppercase micro-labels with an orange required marker.
    '& .fui-Label': {
      fontFamily: brandFonts.mono,
      fontSize: tokens.fontSizeBase100,
      fontWeight: tokens.fontWeightRegular,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: transformrBrand.grey700,
    },
    // Replace Fluent's "*" with the word "REQUIRED" (HTML .req): collapse the
    // asterisk glyph, render the word in mono orange via ::after.
    '& .fui-Label__required': {
      fontSize: 0,
    },
    '& .fui-Label__required::after': {
      content: '"REQUIRED"',
      fontFamily: brandFonts.mono,
      fontSize: tokens.fontSizeBase100,
      letterSpacing: '0.14em',
      color: transformrBrand.orange,
      marginLeft: tokens.spacingHorizontalXS,
    },
    // Hairline white controls with navy text (Combobox/Dropdown/Input/Textarea).
    '& .fui-Input, & .fui-Combobox, & .fui-Dropdown, & .fui-Textarea': {
      backgroundColor: transformrBrand.white,
      borderRadius: tokens.borderRadiusMedium,
      borderTop: `1px solid ${transformrBrand.grey300}`,
      borderRight: `1px solid ${transformrBrand.grey300}`,
      borderLeft: `1px solid ${transformrBrand.grey300}`,
      borderBottom: `1px solid ${transformrBrand.grey300}`,
    },
    '& .fui-Input > input, & .fui-Combobox > input, & .fui-Dropdown > button, & .fui-Textarea > textarea': {
      color: transformrBrand.navy,
    },
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  sidebarCard: {
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: 'none',
    padding: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  // Numbered "how it works" steps (HTML .rdy-step).
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  step: {
    display: 'grid',
    gridTemplateColumns: '30px 1fr',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  stepIx: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.greenInk,
    backgroundColor: transformrBrand.greenSoft,
  },
  stepBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  stepTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.navy,
  },
  stepDesc: {
    color: transformrBrand.grey700,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  tipList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    padding: '0 0 0 20px',
  },
  submitRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${transformrBrand.grey200}`,
  },
  // Form buttons are not bold (overrides the global 600 button weight).
  submitButton: {
    fontWeight: tokens.fontWeightRegular,
  },
  // "Use this example" — green-soft tile (same tone as the step numbers).
  useExampleBtn: {
    fontWeight: tokens.fontWeightRegular,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: transformrBrand.greenSoft,
    color: transformrBrand.greenInk,
    border: `1px solid ${transformrBrand.greenAlt}`,
    ':hover': { backgroundColor: transformrBrand.greenAlt, color: transformrBrand.navy },
  },
  // Mono "ready" status note (HTML .rdy-form-note).
  formNote: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.04em',
    color: transformrBrand.greenInk,
  },
  customIndustryField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  questionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXS,
  },
  questionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  questionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  workedExample: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalXL,
    backgroundColor: transformrBrand.grey100,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${transformrBrand.grey200}`,
  },
  workedExampleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  // The example quote in the heading font, navy, with a left accent bar.
  workedExampleText: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase500,
    color: transformrBrand.navy,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `2px solid ${transformrBrand.greenAlt}`,
  },
  // Prompt library — grouped bordered rows with a "+" add affordance.
  promptGroupLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  promptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  promptRow: {
    display: 'grid',
    gridTemplateColumns: '22px 1fr',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    padding: tokens.spacingHorizontalM,
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    color: transformrBrand.textDark,
    cursor: 'pointer',
    ':hover': {
      border: `1px solid ${transformrBrand.greenAlt}`,
      backgroundColor: transformrBrand.greenSoft,
    },
  },
  // Selected (inserted) prompt — stays in the light-green state until removed.
  promptRowSelected: {
    border: `1px solid ${transformrBrand.greenAlt}`,
    backgroundColor: transformrBrand.greenSoft,
    color: transformrBrand.greenInk,
    fontWeight: tokens.fontWeightSemibold,
  },
  promptAddIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${transformrBrand.grey300}`,
    color: transformrBrand.grey700,
  },
  promptAddIconSelected: {
    border: `1px solid ${transformrBrand.greenAlt}`,
    backgroundColor: transformrBrand.greenAlt,
    color: transformrBrand.white,
  },
  divider: {
    border: 'none',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    margin: 0,
  },
  // Confirmation dialog — mirrors the Ops Manager "Add Project From Transformr"
  // dialog: kicker (accent rule + tracked label) → navy hero title → body sub.
  dlgSurface: {
    padding: 0, // head/body/foot own their padding
    maxWidth: '39.5rem', // 632px (32rem base + 120px)
    width: '100%',
    overflow: 'hidden', // clip the section borders to the rounded corners
  },
  dlgShell: {
    display: 'flex',
    flexDirection: 'column',
  },
  dlgHead: {
    position: 'relative',
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    backgroundColor: transformrBrand.white,
    borderBottom: `1px solid ${transformrBrand.grey200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  dlgKicker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  dlgKickerRule: {
    display: 'inline-block',
    width: '2rem', // 32px accent rule — layout dim, no token
    height: '0.125rem', // 2px
    backgroundColor: transformrBrand.green,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  dlgTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.02em',
    color: transformrBrand.navy,
    margin: 0,
  },
  dlgSub: {
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground2,
    margin: 0,
    marginTop: tokens.spacingVerticalXS,
  },
  dlgClose: {
    position: 'absolute',
    top: tokens.spacingVerticalL,
    right: tokens.spacingHorizontalL,
    color: transformrBrand.grey700,
  },
  dlgBody: {
    backgroundColor: transformrBrand.white,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  // Preview of the example that will replace the current text.
  dlgQuote: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase500,
    color: transformrBrand.navy,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `2px solid ${transformrBrand.greenAlt}`,
  },
  dlgFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${transformrBrand.grey200}`,
    backgroundColor: transformrBrand.white,
  },
  dlgFootNote: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.04em',
    color: transformrBrand.grey700,
  },
  dlgFootActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

export function HomePage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user } = useAuth();
  const setFormData = useDiscoveryStore((s) => s.setFormData);
  const resetRun = useDiscoveryStore((s) => s.reset);
  const { acquireApiToken } = useGraphConsent();

  // Form state
  const [industry, setIndustry] = useState<Industry | ''>('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [userRole, setUserRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [country, setCountry] = useState('Australia');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [aiGoals, setAiGoals] = useState('');
  // Confirmation before the worked example overwrites existing input.
  const [exampleConfirmOpen, setExampleConfirmOpen] = useState(false);
  // Report generation runs in a modal opened from this form (not a route).
  const [runOpen, setRunOpen] = useState(false);

  // Opt-in SharePoint sites (Mode B). Empty = form-only report (Mode A).
  const [selectedSites, setSelectedSites] = useState<SiteSummary[]>([]);

  // Pre-canned prompts + worked example, framed for the chosen role.
  const effectiveRole = userRole === 'other' ? customRole.trim() : userRole;
  const { data: industryQuestions } = useIndustryQuestions(industry, effectiveRole);

  // Profile loading
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Rate limit
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  // Load saved profile on mount
  useEffect(() => {
    if (profileLoaded || !user) return;

    let cancelled = false;

    async function loadProfile() {
      setIsLoadingProfile(true);
      try {
        const apiToken = await acquireApiToken();
        if (!apiToken || cancelled) return;

        const [profileResult, rateLimitResult] = await Promise.all([
          getProfile(apiToken),
          checkRateLimit(apiToken),
        ]);

        if (cancelled) return;

        if (profileResult?.profile) {
          const p = profileResult.profile;
          if (p.industry) setIndustry(p.industry as Industry);
          if (p.custom_industry) setCustomIndustry(p.custom_industry);
          if (p.company_name) setCompanyName(p.company_name);
          if (p.company_size) setCompanySize(p.company_size);
          if (p.user_role) {
            const knownRole = USER_ROLE_OPTIONS.find((o) => o.value === p.user_role);
            if (knownRole) {
              setUserRole(p.user_role);
            } else {
              setUserRole('other');
              setCustomRole(p.user_role);
            }
          }
          if (p.country) setCountry(p.country);
          if (p.website_url) setWebsiteUrl(p.website_url);
          if (p.ai_goals) setAiGoals(p.ai_goals);
        }

        if (rateLimitResult && !rateLimitResult.allowed) {
          setRateLimitError(
            rateLimitResult.reason
              || "You've reached your report limit. Let's talk — book a meeting and we'll go deeper with you."
          );
        }
      } catch {
        // Profile loading is best-effort
      } finally {
        if (!cancelled) {
          setIsLoadingProfile(false);
          setProfileLoaded(true);
        }
      }
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [user, profileLoaded, acquireApiToken]);

  const isValid = industry !== '' && companyName.trim() !== '' && companySize !== ''
    && userRole !== '' && (userRole !== 'other' || customRole.trim() !== '')
    && aiGoals.trim() !== '' && websiteUrl.trim() !== '';

  // Toggle a pre-canned prompt in/out of the free-text goals. "Selected" is
  // derived from the textbox content (prompt text present), so it stays in sync
  // even if the user edits/clears the box — and a selected prompt can be removed
  // again by clicking it.
  const togglePrompt = useCallback((question: SuggestedQuestion) => {
    setAiGoals((prev) => {
      if (prev.includes(question.text)) {
        // Remove the snippet and tidy up leftover blank lines.
        const next = prev.split(question.text).join('').replace(/\n{3,}/g, '\n\n');
        return next.replace(/^\s+/, '').replace(/\s+$/, '');
      }
      const separator = prev.trim() ? '\n\n' : '';
      return (prev + separator + question.text).slice(0, 4000);
    });
  }, []);

  // Prefill the textarea with the industry's worked example. If the user has
  // already written something, confirm first (custom dialog) so we never
  // silently discard their input; otherwise apply it straight away.
  const applyWorkedExample = useCallback(() => {
    const example = industryQuestions?.sampleIntake?.ai_goals?.trim();
    if (!example) return;
    setAiGoals(example.slice(0, 4000));
  }, [industryQuestions]);

  const useWorkedExample = useCallback(() => {
    const example = industryQuestions?.sampleIntake?.ai_goals?.trim();
    if (!example) return;
    if (aiGoals.trim()) {
      setExampleConfirmOpen(true);
      return;
    }
    applyWorkedExample();
  }, [industryQuestions, aiGoals, applyWorkedExample]);

  const confirmWorkedExample = useCallback(() => {
    applyWorkedExample();
    setExampleConfirmOpen(false);
  }, [applyWorkedExample]);

  const handleSubmit = useCallback(() => {
    if (!isValid || !industry) return;

    const userContext: UserContext = {
      user_role: userRole === 'other' ? customRole.trim() : userRole,
      company_size: companySize,
      ai_goals: aiGoals,
      company_name: companyName.trim(),
      country,
      website_url: websiteUrl.trim(),
      ...(industry === 'other' && customIndustry.trim() ? { custom_industry: customIndustry.trim() } : {}),
    };

    // Adding sites opts into the SharePoint crawl (Mode B); otherwise form-only.
    const scanMode = selectedSites.length > 0 ? 'sharepoint' : 'form_only';
    // Clear any prior run, seed the form data, then open the run modal — the
    // analysis auto-starts inside the dialog (no route change).
    resetRun();
    setFormData(industry, userContext, true, scanMode, selectedSites.map((s) => s.id));
    setRunOpen(true);
  }, [industry, customIndustry, userRole, customRole, companySize, aiGoals, companyName, country, websiteUrl, selectedSites, isValid, setFormData, resetRun]);

  // Re-check the rolling caps after a run closes — a just-generated report may
  // have hit the limit, so refresh the gate (and its "talk to us" CTA).
  const refreshRateLimit = useCallback(async () => {
    try {
      const token = await acquireApiToken();
      if (!token) return;
      const r = await checkRateLimit(token);
      setRateLimitError(r && !r.allowed
        ? (r.reason || "You've reached your report limit. Let's talk — book a meeting and we'll go deeper with you.")
        : null);
    } catch { /* best-effort */ }
  }, [acquireApiToken]);

  return (
    <div className={styles.page}>
      <PageBanner
        kicker="AI Readiness · Intake"
        title={`Generate your AI Readiness Report${user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}`}
        subtitle="A personalised, scored view of where your organisation stands on AI readiness — and a clear path forward. Built from a few details below; no environment access required."
      />

      {rateLimitError && (
        <MessageBar intent="info">
          <MessageBarBody>{rateLimitError}</MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="primary"
              size="large"
              icon={<CalendarRegular />}
              onClick={() => navigate('/book')}
            >
              Book a Meeting
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      <div className={styles.layout}>
        {/* Left column — form */}
        <Card className={styles.formCard}>
          <div className={styles.cardHead}>
            <span className={styles.eyebrow}>
              <DocumentBulletListRegular fontSize={14} />
              Tell us about your organisation
            </span>
            <h2 className={styles.cardTitle}>The details below build your report</h2>
            <Body1 className={styles.cardDesc}>
              We read your industry, size and goals, then map the AI opportunities and
              governance steps that matter most for organisations like yours.
            </Body1>
          </div>

          {isLoadingProfile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              <Caption1>Loading your saved details...</Caption1>
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label required htmlFor="industry">Industry</Label>
              <Combobox
                id="industry"
                placeholder="Search or select your industry"
                value={industry ? INDUSTRY_OPTIONS.find((o) => o.value === industry)?.label ?? '' : ''}
                selectedOptions={industry ? [industry] : []}
                onOptionSelect={(_, data) => {
                  setIndustry(data.optionValue as Industry);
                  if (data.optionValue !== 'other') setCustomIndustry('');
                }}
                freeform={false}
                disabled={isLoadingProfile}
              >
                {INDUSTRY_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    <div>
                      <Text size={300} weight="semibold">{opt.label}</Text>
                      <br />
                      <Caption1>{opt.description}</Caption1>
                    </div>
                  </Option>
                ))}
              </Combobox>
              {industry === 'other' && (
                <div className={styles.customIndustryField}>
                  <Input
                    id="customIndustry"
                    placeholder="Describe your industry (optional)"
                    value={customIndustry}
                    onChange={(_, data) => setCustomIndustry(data.value.slice(0, 32))}
                    maxLength={32}
                    disabled={isLoadingProfile}
                  />
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'right' }}>
                    {customIndustry.length}/32
                  </Caption1>
                </div>
              )}
            </div>

            <div className={styles.field}>
              <Label required htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                placeholder="e.g. Acme Construction Pty Ltd"
                value={companyName}
                onChange={(_, data) => setCompanyName(data.value.slice(0, 200))}
                maxLength={200}
                disabled={isLoadingProfile}
              />
            </div>

            <div className={styles.field}>
              <Label required htmlFor="companySize">Number of Employees</Label>
              <Dropdown
                id="companySize"
                placeholder="Select company size"
                value={companySize ? COMPANY_SIZE_OPTIONS.find((o) => o.value === companySize)?.label ?? '' : ''}
                selectedOptions={companySize ? [companySize] : []}
                onOptionSelect={(_, data) => setCompanySize(data.optionValue as string)}
                disabled={isLoadingProfile}
              >
                {COMPANY_SIZE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <div className={styles.field}>
              <Label required htmlFor="userRole">Your Role</Label>
              <Dropdown
                id="userRole"
                placeholder="Select your role"
                value={userRole ? USER_ROLE_OPTIONS.find((o) => o.value === userRole)?.label ?? '' : ''}
                selectedOptions={userRole ? [userRole] : []}
                onOptionSelect={(_, data) => {
                  setUserRole(data.optionValue as string);
                  if (data.optionValue !== 'other') setCustomRole('');
                }}
                disabled={isLoadingProfile}
              >
                {USER_ROLE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
              {userRole === 'other' && (
                <div className={styles.customIndustryField}>
                  <Input
                    id="customRole"
                    placeholder="Describe your role"
                    value={customRole}
                    onChange={(_, data) => setCustomRole(data.value.slice(0, 200))}
                    maxLength={200}
                    required
                    disabled={isLoadingProfile}
                  />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <Label htmlFor="country">Country</Label>
              <Dropdown
                id="country"
                placeholder="Select country"
                value={country}
                selectedOptions={[country]}
                onOptionSelect={(_, data) => setCountry(data.optionValue as string)}
                disabled={isLoadingProfile}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <Option key={c} value={c} text={c}>{c}</Option>
                ))}
              </Dropdown>
            </div>

            <div className={styles.field}>
              <Label required htmlFor="websiteUrl">Company Website</Label>
              <Input
                id="websiteUrl"
                placeholder="e.g. https://www.example.com.au"
                value={websiteUrl}
                onChange={(_, data) => setWebsiteUrl(data.value.slice(0, 200))}
                maxLength={200}
                type="url"
                disabled={isLoadingProfile}
              />
            </div>

            <div className={mergeClasses(styles.field, styles.fullWidth)}>
              <Label required htmlFor="aiGoals">
                How are you thinking about AI?
              </Label>
              <Textarea
                id="aiGoals"
                placeholder={"e.g. How do I ensure AI agents only access data they should? Can AI be trusted with sensitive financial or HR information? What governance do I need before deploying Copilot or custom agents?"}
                value={aiGoals}
                onChange={(_, data) => setAiGoals(data.value.slice(0, 4000))}
                resize="vertical"
                rows={6}
                maxLength={4000}
                disabled={isLoadingProfile}
              />
            </div>

          </div>

          <div className={styles.submitRow}>
            {isValid && !rateLimitError ? (
              <span className={styles.formNote}>
                <ShieldCheckmarkRegular fontSize={14} />
                Ready
              </span>
            ) : (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Fill in the required fields to continue
              </Caption1>
            )}
            <Button
              appearance="primary"
              size="large"
              className={styles.submitButton}
              icon={<SparkleRegular />}
              onClick={handleSubmit}
              disabled={!isValid || !!rateLimitError || isLoadingProfile}
            >
              Generate My Report
            </Button>
          </div>

          {industry !== '' && industryQuestions?.sampleIntake?.ai_goals && (
            <div className={styles.workedExample}>
              <div className={styles.workedExampleHeader}>
                <span className={styles.eyebrow}>
                  <LightbulbRegular fontSize={14} />
                  A worked example for your industry
                </span>
                <Button
                  size="small"
                  appearance="secondary"
                  className={styles.useExampleBtn}
                  onClick={useWorkedExample}
                  disabled={isLoadingProfile}
                >
                  Use this example
                </Button>
              </div>
              <blockquote className={styles.workedExampleText}>
                “{industryQuestions.sampleIntake.ai_goals}”
              </blockquote>
            </div>
          )}

          <Card className={styles.sectionCard}>
            <div className={styles.sidebarHeader}>
              <ShieldCheckmarkRegular fontSize={20} color={tokens.colorBrandForeground1} />
              <Text weight="semibold" size={300}>Permissions</Text>
            </div>
            <Caption1>
              Signing in asks only for your basic profile — no admin consent required.
              We never access your organisation's content.
            </Caption1>
            <ul className={styles.tipList}>
              <li><Caption1>Your name and email, to personalise the report</Caption1></li>
              <li><Caption1>No access to your files, documents, or organisational content</Caption1></li>
            </ul>
            <Caption1 style={{ color: tokens.colorNeutralForeground4, fontSize: tokens.fontSizeBase100 }}>
              Sign-in scope: User.Read — your basic profile only.
            </Caption1>
          </Card>

          {environmentScanEnabled && (
            <>
              <hr className={styles.divider} />
              <SitePicker selectedSites={selectedSites} onChange={setSelectedSites} />
            </>
          )}

          <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.5' }}>
            {environmentScanEnabled ? (
              <>
                By default your report is built from the details you provide above — we do
                not access your Microsoft 365 environment. If you choose to add SharePoint
                sites, Transformr uses read-only access to review metadata for only those
                sites (site names, file types, and content structure), and your report
                includes an audit trail of exactly what was accessed. Reports are stored
                securely for up to 6 months so you can download them, after which they are
                automatically deleted. Your profile details are saved to personalise future
                reports, and you can delete your reports at any time.{' '}
              </>
            ) : (
              <>
                Your report is built from the details you provide above — we do not access
                your Microsoft 365 environment. Reports are stored securely for up to 6
                months so you can download them, after which they are automatically deleted.
                Your profile details are saved to personalise future reports, and you can
                delete your reports at any time.{' '}
              </>
            )}
            <a href="/data-privacy" style={{ color: tokens.colorBrandForeground1 }}>
              Learn more about your data
            </a>.
          </Caption1>
        </Card>

        {/* Right column — guidance */}
        <div className={styles.sidebar}>
          <Card className={styles.sidebarCard}>
            <div className={styles.cardHead}>
              <span className={styles.eyebrow}>
                <InfoRegular fontSize={14} />
                How it works
              </span>
              <h2 className={styles.cardTitle}>60 seconds to your report</h2>
            </div>
            <div className={styles.steps}>
              <div className={styles.step}>
                <span className={styles.stepIx}>01</span>
                <div className={styles.stepBody}>
                  <span className={styles.stepTitle}>Tell us about your organisation</span>
                  <Caption1 className={styles.stepDesc}>
                    Industry, size, role and what you want AI to do.
                  </Caption1>
                </div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepIx}>02</span>
                <div className={styles.stepBody}>
                  <span className={styles.stepTitle}>We map your context</span>
                  <Caption1 className={styles.stepDesc}>
                    Against industry benchmarks and real-world AI use cases.
                  </Caption1>
                </div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepIx}>03</span>
                <div className={styles.stepBody}>
                  <span className={styles.stepTitle}>Get your report</span>
                  <Caption1 className={styles.stepDesc}>
                    A personalised view with a clear path forward.
                  </Caption1>
                </div>
              </div>
            </div>
          </Card>

          {industry !== '' && industryQuestions &&
            (industryQuestions.commonScenarios.length > 0 ||
              industryQuestions.governanceCompliance.length > 0) && (
            <Card className={styles.sidebarCard}>
              <div className={styles.cardHead}>
                <span className={styles.eyebrow}>
                  <LightbulbRegular fontSize={14} />
                  Prompt library
                </span>
                <h2 className={styles.cardTitle}>Need a starting point?</h2>
              </div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Not sure what to write? Tap a prompt to add it to your AI goals
                (tap again to remove), then answer it in your own words.
              </Caption1>
              {industryQuestions.commonScenarios.length > 0 && (
                <div className={styles.questionGroup}>
                  <span className={styles.promptGroupLabel}>Productivity &amp; time saved</span>
                  <div className={styles.promptList}>
                    {industryQuestions.commonScenarios.map((q) => {
                      const selected = aiGoals.includes(q.text);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          aria-pressed={selected}
                          title={selected ? 'Added — click to remove' : 'Click to add'}
                          className={mergeClasses(styles.promptRow, selected && styles.promptRowSelected)}
                          onClick={() => togglePrompt(q)}
                          disabled={isLoadingProfile}
                        >
                          <span className={mergeClasses(styles.promptAddIcon, selected && styles.promptAddIconSelected)}>
                            {selected ? <CheckmarkRegular fontSize={14} /> : <AddRegular fontSize={14} />}
                          </span>
                          <span>{q.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {industryQuestions.governanceCompliance.length > 0 && (
                <div className={styles.questionGroup}>
                  <span className={styles.promptGroupLabel}>Governance &amp; compliance</span>
                  <div className={styles.promptList}>
                    {industryQuestions.governanceCompliance.map((q) => {
                      const selected = aiGoals.includes(q.text);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          aria-pressed={selected}
                          title={selected ? 'Added — click to remove' : 'Click to add'}
                          className={mergeClasses(styles.promptRow, selected && styles.promptRowSelected)}
                          onClick={() => togglePrompt(q)}
                          disabled={isLoadingProfile}
                        >
                          <span className={mergeClasses(styles.promptAddIcon, selected && styles.promptAddIconSelected)}>
                            {selected ? <CheckmarkRegular fontSize={14} /> : <AddRegular fontSize={14} />}
                          </span>
                          <span>{q.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Card className={styles.sidebarCard}>
            <div className={styles.sidebarHeader}>
              <LightbulbRegular fontSize={20} color={tokens.colorBrandForeground1} />
              <Text weight="semibold" size={300}>Get the Best Results</Text>
            </div>
            <ul className={styles.tipList}>
              <li>
                <Caption1>
                  The AI goals field has the biggest impact on your report. The more
                  specific you are about what you want AI to do, the more targeted
                  your recommendations will be.
                </Caption1>
              </li>
              <li>
                <Caption1>
                  Mention the systems and workflows your team relies on most — the
                  report will speak to your day-to-day, not generic advice.
                </Caption1>
              </li>
              <li>
                <Caption1>
                  Adding your company website lets us research what your
                  organisation actually does and tailor opportunities to your
                  specific business.
                </Caption1>
              </li>
            </ul>
          </Card>

          <Button
            appearance="subtle"
            icon={<DocumentBulletListRegular />}
            onClick={() => navigate('/reports')}
            style={{ justifyContent: 'flex-start' }}
          >
            View Previous Reports
          </Button>
        </div>
      </div>

      {/* Replace-with-worked-example confirmation — Ops Manager dialog style. */}
      <Dialog
        open={exampleConfirmOpen}
        onOpenChange={(_, d) => setExampleConfirmOpen(d.open)}
      >
        <DialogSurface className={styles.dlgSurface}>
          <div className={styles.dlgShell}>
            <div className={styles.dlgHead}>
              <span className={styles.dlgKicker}>
                <span className={styles.dlgKickerRule} aria-hidden="true" />
                Prefill · Worked Example
              </span>
              <h2 className={styles.dlgTitle}>Use This Worked Example?</h2>
              <p className={styles.dlgSub}>
                This replaces what you&apos;ve written in the AI goals field with the
                worked example for your industry. You can edit it afterwards.
              </p>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close dialog"
                className={styles.dlgClose}
                onClick={() => setExampleConfirmOpen(false)}
              />
            </div>

            {industryQuestions?.sampleIntake?.ai_goals && (
              <div className={styles.dlgBody}>
                <blockquote className={styles.dlgQuote}>
                  “{industryQuestions.sampleIntake.ai_goals}”
                </blockquote>
              </div>
            )}

            <div className={styles.dlgFoot}>
              <span className={styles.dlgFootNote}>
                Your current answer will be replaced
              </span>
              <div className={styles.dlgFootActions}>
                <Button appearance="secondary" onClick={() => setExampleConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button appearance="primary" onClick={confirmWorkedExample}>
                  Use Example
                </Button>
              </div>
            </div>
          </div>
        </DialogSurface>
      </Dialog>

      {/* Report generation runs here, in place — progress → "Your Report is Ready". */}
      <ReportRunDialog
        open={runOpen}
        onClose={() => { setRunOpen(false); resetRun(); refreshRateLimit(); }}
      />
    </div>
  );
}
